import React, { useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import { useFetchDocumentDetails } from "../../API/Queries/useFetchDocumentDetails";
import { useFetchMultiple } from "../../API/Queries/useFetchMultiple";
import { useFetchStateData } from "../../API/Queries/useFetchStateData";
import { useFetchCityData } from "../../API/Queries/useFetchCityData";
import { useFetchMarketData } from "../../API/Queries/useFetchMarketData";
import { useUpdateFacility } from "../../API/Queries/useUpdateFacility";
import Loader from "../../Components/Loader";
import GeneralToast from "../../Components/Toaster";
import Select from "react-select";
import styles from "./index.css";

const MarketUpdate = () => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [searchMobile, setSearchMobile] = useState("");
  const [org, setOrg] = useState(null);
  const [profile, setProfile] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [lastAutoGeneratedId, setLastAutoGeneratedId] = useState(null);
  const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);

  const {
    data: userData,
    error: fetchError,
    isLoading: contactFetchLoading,
    mutate: dataFetch,
  } = useFetchDocumentDetails();

  const {
    data: multipleData,
    isSuccess: multipleSuccess,
    isLoading: multipleLoading,
    mutate: fetchMultipleDetails,
  } = useFetchMultiple();

  useEffect(() => {
    const autoGeneratedId = userData?.data?.autoGeneratedId;
    if (autoGeneratedId) {
      setLastAutoGeneratedId(autoGeneratedId);
      fetchMultipleDetails([autoGeneratedId]);
    }
  }, [userData, fetchMultipleDetails]);

  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [cityError, setCityError] = useState("");
  const [areaError, setAreaError] = useState("");

  const { data: stateData, mutate: fetchStateData } = useFetchStateData();
  const { data: cityData, mutate: fetchCityData } = useFetchCityData();
  const { data: marketData, mutate: fetchMarketData } = useFetchMarketData();

  useEffect(() => {
    if (selectedState?.value) {
      fetchCityData(selectedState.value);
    }
  }, [selectedState, fetchCityData]);

  useEffect(() => {
    if (selectedCity?.value) {
      fetchMarketData({ input: { cityId: selectedCity.value } });
    }
  }, [selectedCity, fetchMarketData]);

  const [initialValues, setInitialValues] = useState({
    state: null,
    city: null,
    area: null,
  });

  const handleStateChange = (option) => {
    setSelectedState(option);
    setSelectedCity(null);
    setSelectedArea(null);
    setCityError("City is required.");
    setAreaError("Area of Operation is required.");
  };

  const handleCityChange = (option) => {
    setSelectedCity(option);
    setSelectedArea(null);
    setCityError("");
    setAreaError("Area of Operation is required.");
  };

  const handleAreaChange = (option) => {
    setSelectedArea(option);
    setAreaError("");
  };

  useEffect(() => {
    if (
      multipleSuccess &&
      Array.isArray(multipleData) &&
      multipleData.length > 0
    ) {
      const orgDetail = multipleData[0].organization_detail;
      const profileDetail = multipleData[0].profile_detail;
      setOrg(orgDetail);
      setProfile(profileDetail);

      const stateValue = orgDetail?.organization_state
        ? {
            value: orgDetail.organization_state?.state_id,
            label: orgDetail.organization_state?.state_name,
          }
        : null;
      const cityValue = orgDetail?.organization_city
        ? {
            value: orgDetail.organization_city?.city_id,
            label: orgDetail.organization_city?.city_name,
          }
        : null;
      const areaValue = orgDetail?.organization_facility
        ? {
            value: orgDetail.organization_facility?.id,
            label: orgDetail.organization_facility?.name,
          }
        : null;

      setSelectedState(stateValue);
      setSelectedCity(cityValue);
      setSelectedArea(areaValue);
      setInitialValues({
        state: stateValue,
        city: cityValue,
        area: areaValue,
      });
    } else {
      setProfile(null);
      setSelectedState(null);
      setSelectedCity(null);
      setSelectedArea(null);
    }
  }, [multipleSuccess, multipleData]);

  const handleSearchQueryChange = ({ target: { value } }) => {
    if (!/^\d*$/.test(value)) return;
    setSearchQuery(value);
  };

  const handleSearch = () => {
    if (!/^\d{10}$/.test(searchQuery.trim())) {
      setErrorMessage("Please enter a valid 10-digit mobile number.");
      return;
    }
    setProfile(null);
    setErrorMessage("");
    setSearchMobile(searchQuery);
    fetchStateData();
    setSuccessMessage("");
    setIsSaveSuccessful(false);
    dataFetch({ input: { contactNumber: searchQuery } });
  };

  const { mutate: updateFacility, isLoading: isSaving } = useUpdateFacility();
  const handleSave = () => {
    let hasError = false;

    if (!selectedCity) {
      setCityError("City is required.");
      hasError = true;
    }
    if (!selectedArea) {
      setAreaError("Area of Operation is required.");
      hasError = true;
    }
    if (hasError) return;

    const systemUserId = userData?.data?.systemUserId;
    const realmID = userData?.data?.id;
    const id = userData?.data?.autoGeneratedId;

    if (!systemUserId || !realmID || !id) {
      return;
    }

    const payload = {
      organization_name: org?.organization_name,
      email: org?.organization_email?.email,
      phone_country: org?.organization_phone_number?.countryCode,
      phone_ext: org?.organization_phone_number?.ext,
      phone_number: searchMobile,
      organization_address_line1: org?.organization_address_line1,
      organization_address_line2: org?.organization_address_line2,
      organization_country: org?.organization_country?.country_id,
      organization_state:
        selectedState?.value || org?.organization_state?.state_id,
      organization_city: selectedCity?.value || org?.organization_city?.city_id,
      organization_facility:
        selectedArea?.value || org?.organization_facility?.id,
      organization_industry: org?.organization_industry_id,
      organization_pin_code: org?.organization_pin_code,
      organization_currency: org?.organization_currency?._id,
      organization_time_zone: org?.organization_time_zone?._id,
      media:
        profile?.media?.map((ele) => ({
          media_id: ele.media_id?.mediaId || null,
          mediaURL: ele.media_id?.media_url || "",
          type: ele.type || "",
        })) || [],
    };

    updateFacility(
      { payload, systemUserId, realmID, id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["fetchUserDetails", searchQuery]);
          setSuccessMessage("Details updated successfully!");
          setIsSaveSuccessful(true);
          setProfile(null);
          setSelectedState(null);
          setSelectedCity(null);
          setSelectedArea(null);
          setSearchQuery("");
          setErrorMessage("");
        },
        onError: (error) => {
          if (error?.response?.status === 400) {
            setSelectedState(initialValues.state);
            setSelectedCity(initialValues.city);
            setSelectedArea(initialValues.area);
            setErrorMessage(
              error?.response?.data?.errorMessage ||
                "Something went wrong. Please try again."
            );
          }
        },
      }
    );
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      width: "100%",
      border: '1px solid ${state.isFocused ? "#0e76a8" : "#cccccc"}',
      borderRadius: "4px",
      boxShadow: state.isFocused ? "0 0 5px rgba(14, 118, 168, 0.5)" : "none",
      transition: "border-color 0.3s ease",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected
        ? "#0e76a8"
        : isFocused
        ? "#0e76a861"
        : "#ffffff",
      color: isSelected ? "white" : "black",
      padding: 10,
    }),
  };

  return (
    <>
      {(contactFetchLoading || multipleLoading || isSaving) && <Loader />}
      {successMessage && (
        <GeneralToast type="success" message={successMessage} />
      )}
      {((!profile && multipleSuccess && !isSaveSuccessful) || errorMessage) && (
        <GeneralToast
          type="error"
          message={errorMessage || "Profile is not created."}
        />
      )}
      {(fetchError ||
        (userData?.data && Object.keys(userData.data).length === 0)) && (
        <GeneralToast type="error" message="User not found!" />
      )}
      <div className="container-user">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Mobile Number"
            value={searchQuery}
            onChange={handleSearchQueryChange}
            maxLength="10"
          />
          <button
            onClick={handleSearch}
            disabled={!searchQuery.trim() || contactFetchLoading}
          >
            Search
          </button>
        </div>
        {userData?.data && Object.keys(userData.data).length > 0 && profile && (
          <div className={styles.dropdownSection}>
            <h3 className="text-center font-bold text-lg">
              Update Market Details
            </h3>
            <div
              className={styles.formGroupStyle}
              style={{ marginBottom: "20px" }}
            >
              <label htmlFor="state" className={styles.labelStyle}>
                State: <span className="text-red-700">*</span>
              </label>
              <Select
                styles={selectStyles}
                value={selectedState}
                onChange={handleStateChange}
                options={stateData?.data?.map((state) => ({
                  value: state.state_id,
                  label: state.state_name,
                }))}
                placeholder="Select State"
              />
            </div>
            <div
              className={styles.formGroupStyle}
              style={{ marginBottom: "20px" }}
            >
              <label htmlFor="city" className={styles.labelStyle}>
                City: <span className="text-red-700">*</span>
              </label>
              <Select
                styles={selectStyles}
                value={selectedCity}
                onChange={handleCityChange}
                options={cityData?.data?.map((city) => ({
                  value: city.city_id,
                  label: city.city_name,
                }))}
                placeholder="Select City"
                isDisabled={!selectedState}
              />
              {cityError && (
                <p className="text-red-700 text-sm mt-1">{cityError}</p>
              )}
            </div>
            <div
              className={styles.formGroupStyle}
              style={{ marginBottom: "20px" }}
            >
              <label htmlFor="area" className={styles.labelStyle}>
                Area of Operation: <span className="text-red-700">*</span>
              </label>
              <Select
                styles={selectStyles}
                value={selectedArea}
                onChange={handleAreaChange}
                options={marketData?.data?.map((market) => ({
                  value: market.id,
                  label: market.name,
                }))}
                placeholder="Select Area"
                isDisabled={!selectedCity}
              />
              {areaError && (
                <p className="text-red-700 text-sm mt-1">{areaError}</p>
              )}
            </div>
            <div style={{ marginTop: "20px" }}>
              <button
                type="button"
                className={styles.saveButton}
                style={{
                  width: "100%",
                  padding: "12px",
                  backgroundColor: "#0e76a8",
                  border: "none",
                  color: "#fff",
                  borderRadius: "4px",
                  marginTop: "10px",
                }}
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MarketUpdate;
